import { useCallback, useState } from 'react';

import { listAdmissions } from '../../services/api/admissionApi';
import { invalidateAdmissionServerSearch } from './useAdmissionServerSearch';
import { ADMISSION_STATUS, TERMINAL_STATUSES } from '../../constants/admission';

/**
 * useAdmissionSearchIndex — the warm-up cache of NON-TERMINAL admissions. It
 * feeds the desk count badges (computeDeskCounts) and instant local suggestion
 * matches; discharged/cancelled history is found by the server search
 * (useAdmissionServerSearch), never by this index.
 *
 * RUNTIME SAFETY (this exact class of bug caused the A2 request-storm):
 *   - SINGLE-FLIGHT: concurrent warm() callers share ONE in-flight promise
 *     (module-level `inFlight`); once resolved the merged rows are module-cached,
 *     so a warm() on every focus is a cache hit (0 calls) — never a new fan-out.
 *   - BOUNDED FAN-OUT: one list call per non-terminal status (7), issued in
 *     sequential CHUNKS of 3 so peak concurrency stays low.
 *   - Typing NEVER hits the network: matching is in-memory over the cached rows.
 *   - invalidate() drops the cache after any desk action (the visible rows stay
 *     until replaced) so the NEXT focus re-warms with fresh data.
 */

const WARM_STATUSES = Object.values(ADMISSION_STATUS).filter((st) => !TERMINAL_STATUSES.has(st));
const WARM_CHUNK = 3;

// Module-level (per-tab) cache + single-flight promise. Intentionally outside the
// hook so every mounted desk shares ONE warmed index.
const CACHE = { rows: null, inFlight: null };

/** Test-only: reset the module cache between cases. */
export function __resetAdmissionSearchIndex() {
  CACHE.rows = null;
  CACHE.inFlight = null;
}

/** Logout / account switch: drop the warmed index (and the server-search cache) so the next login re-warms its own. */
export function clearAdmissionSearchIndex() {
  CACHE.rows = null;
  CACHE.inFlight = null;
  invalidateAdmissionServerSearch();
}

async function warmUp() {
  const statuses = WARM_STATUSES;
  const merged = new Map();
  for (let i = 0; i < statuses.length; i += WARM_CHUNK) {
    const batch = statuses.slice(i, i + WARM_CHUNK);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.all(
      batch.map((st) => listAdmissions({ status: st })),
    );
    for (const res of results) {
      if (res && res.success && Array.isArray(res.data.admissions)) {
        for (const a of res.data.admissions) {
          if (a && a.admission_id) merged.set(a.admission_id, a);
        }
      }
    }
  }
  return Array.from(merged.values());
}

export function useAdmissionSearchIndex() {
  const [rows, setRows] = useState(CACHE.rows || []);
  const [warming, setWarming] = useState(false);

  const warm = useCallback(async () => {
    if (CACHE.rows) {
      setRows(CACHE.rows);
      return CACHE.rows;
    }
    if (CACHE.inFlight) {
      const shared = await CACHE.inFlight;
      setRows(shared);
      return shared;
    }
    setWarming(true);
    const mine = warmUp();
    CACHE.inFlight = mine;
    try {
      const arr = await mine;
      if (CACHE.inFlight === mine) CACHE.rows = arr; // skip if cleared (logout) mid-flight
      setRows(arr);
      return arr;
    } finally {
      if (CACHE.inFlight === mine) CACHE.inFlight = null;
      setWarming(false);
    }
  }, []);

  const invalidate = useCallback(() => {
    CACHE.rows = null;
    CACHE.inFlight = null;
    invalidateAdmissionServerSearch(); // the 60 s per-query server cache goes stale at the same moment
  }, []);

  return { warm, rows, warming, invalidate };
}
